import { Component, ElementRef, HostListener, forwardRef, inject, input, output, signal } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject, debounceTime, distinctUntilChanged, switchMap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Client } from '../../core/client/client.models';
import { ClientService } from '../../core/client/client.service';

/**
 * Campo de pessoa: procura nos clientes já registados enquanto se escreve e, se não existir
 * nenhum, aceita o que foi escrito — o cliente é criado ao gravar (o backend trata disso).
 *
 * O valor do control é o **id do cliente** escolhido, ou `null` quando o texto não corresponde
 * a nenhum. O nome e o e-mail escritos saem por `nameChange`/`emailChange`, porque a ocorrência
 * guarda-os na mesma: são o que ficou escrito no momento, e o portal público aceita
 * participações anónimas que nunca têm cliente.
 */
@Component({
  selector: 'app-client-picker',
  templateUrl: './client-picker.html',
  styleUrl: './client-picker.scss',
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => ClientPicker), multi: true }],
})
export class ClientPicker implements ControlValueAccessor {
  private readonly clients = inject(ClientService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  /** Texto já escrito (nome do participante), para o campo abrir preenchido na edição. */
  readonly initialName = input<string | null>(null);
  readonly initialEmail = input<string | null>(null);
  /** E-mail atual do participante: sem ele o servidor não cria o cliente, e a frase cala-se. */
  readonly currentEmail = input<string | null>(null);

  readonly nameChange = output<string>();
  readonly emailChange = output<string>();

  protected readonly query = signal('');
  protected readonly results = signal<Client[]>([]);
  protected readonly open = signal(false);
  protected readonly searching = signal(false);
  protected readonly selected = signal<Client | null>(null);

  private readonly terms = new Subject<string>();
  private clientId: number | null = null;
  private onChange: (value: number | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor() {
    this.terms
      .pipe(
        debounceTime(250),
        distinctUntilChanged(),
        switchMap((q) => {
          this.searching.set(true);
          return this.clients.list({ q, size: 8 });
        }),
        takeUntilDestroyed(),
      )
      .subscribe((page) => {
        this.results.set(page.content);
        this.searching.set(false);
        this.open.set(true);
      });
  }

  writeValue(value: number | null): void {
    this.clientId = value ?? null;
    if (value == null) {
      this.selected.set(null);
    }
    // O nome vem por input: na edição o formulário já tem o texto gravado.
    this.query.set(this.initialName() ?? '');
  }

  registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  protected search(text: string): void {
    this.query.set(text);
    this.selected.set(null);
    // Escrever desfaz a escolha: o texto passa a valer por si, e o cliente é criado ao gravar.
    this.clientId = null;
    this.onChange(null);
    this.nameChange.emit(text);
    if (text.trim().length >= 2) {
      this.terms.next(text.trim());
    } else {
      this.results.set([]);
      this.open.set(false);
    }
  }

  protected choose(client: Client): void {
    this.selected.set(client);
    this.clientId = client.id;
    this.query.set(client.name);
    this.open.set(false);
    this.onChange(client.id);
    this.onTouched();
    this.nameChange.emit(client.name);
    this.emailChange.emit(client.email);
  }

  protected blur(): void {
    this.onTouched();
  }

  @HostListener('document:click', ['$event'])
  protected closeOnOutside(event: MouseEvent): void {
    if (!this.host.nativeElement.contains(event.target as Node)) {
      this.open.set(false);
    }
  }
}
